// src/pages/ItemDetails.jsx
import React from "react";
import usePageMeta from "../hooks/usePageMeta";
import { useParams, Navigate } from "react-router-dom";
import { ONTARIO_ONLY_ITEM_IDS, excludesOntarioOnly, getRegistrationProvince } from "../utils/registrationProvince";

// Import all item components
import Profile from "./items/Profile";
import Documents from "./items/Documents";
import TD1FillablePdf from "./items/TD1FillablePdf";
import ProvincialTd1Pdf from "./items/ProvincialTd1Pdf";
import AccessibilityForm from "./items/AccessibilityStndCustmer";
import OvertimeAvg from "./items/OvertimeAvg";
import EsaHours from "./items/OntarioExceedWorkHrs";
import EmploymentStandards from "./items/EmpStndTempEmployees";
import OvertimePay from "./items/OvertimePay";
import HumanRights from "./items/HumanRights";
import MediaRelease from "./items/MediaRelease";
import LabourInfo from "./items/LabourInfo";
import HolidayAck from "./items/HolidayAck";
import PayrollDeduction from "./items/PayrollDeduction";
import WHMISSafety from "./items/WhmisSafety";
import ViolencePolicy from "./items/ViolencePolicy";
import AdditionalInfo from "./items/AdditionalInfo";
import HealthSafety from "./items/HealthSafety";
import WHMIS from "./items/Whmis";
import WorkPref from "./items/WorkPref";
import Availability from "./items/Availability";
import Certifications from "./items/Certifications";

const componentMap = {
  profile: Profile,
  documents: Documents,
  // Federal TD1: official CRA fillable PDF (previous in-app form: ./items/TD1TAX)
  "td1-tax": TD1FillablePdf,
  // Provincial TD1 for the Profile province: official fillable PDF (previous Ontario-only form: ./items/TDONTAX)
  "tdon-tax": ProvincialTd1Pdf,
  accessibility: AccessibilityForm,
  "overtime-avg": OvertimeAvg,
  "esa-hours": EsaHours,
  "employment-standards": EmploymentStandards,
  "overtime-pay": OvertimePay,
  "human-rights": HumanRights,
  "media-release": MediaRelease,
  "labour-info": LabourInfo,
  "holiday-ack": HolidayAck,
  "payroll-deduction": PayrollDeduction,
  "whmis-safety": WHMISSafety,
  "violence-policy": ViolencePolicy,
  "additional-info": AdditionalInfo,
  "health-safety": HealthSafety,
  whmis: WHMIS,
  "work-preferences": WorkPref,
  availability: Availability,
  certifications: Certifications,
};

export default function ItemDetail() {
  const { itemId } = useParams();
  const accountId = localStorage.getItem("accountId"); // ✅ from login/session

  const Component = componentMap[itemId];

  // Title case for the page title from slug
  const title = String(itemId || "").split("-").map(s => s.charAt(0).toUpperCase() + s.slice(1)).join(" ");
  usePageMeta(title || "Item");

  if (!Component) return <div>Item not found</div>;

  // Other sections are locked until Profile is complete (mirrors the gate on the Register page).
  // Register syncs backend statuses into localStorage, so this also covers direct URL access.
  if (itemId !== "profile") {
    const profileStatus =
      (accountId && localStorage.getItem(`status_${accountId}_profile`)) ||
      localStorage.getItem("status_profile");
    if (profileStatus !== "complete") {
      return <Navigate to="/register" replace state={{ profileRequired: true }} />;
    }
  }

  // Ontario-only forms (TDON) are not available to staff in other provinces
  if (ONTARIO_ONLY_ITEM_IDS.includes(itemId) && excludesOntarioOnly(getRegistrationProvince(accountId))) {
    return <Navigate to="/register" replace />;
  }

  return <Component accountId={accountId} />;
}
